import { Flags } from "@/classes/FeatureFlags";

// Searchable Settings destinations. The same visibility predicates are used by
// ClientView for menu rendering and direct-link validation, and by the command
// palette for discovery. Keep access rules here so those three surfaces cannot
// drift apart.
export const settingsSections = [
    {
        id: "details",
        label: "Business details",
        description: "View and manage your business information",
        icon: "building",
        keywords: ["business", "company", "account"],
        group: "account",
        visible: ({ role }) => role("admin") || role("auditor")
    },
    {
        id: "preferences",
        label: "Preferences",
        description: "Configure email reports and intake settings",
        icon: "cog",
        keywords: ["settings", "email reports", "intake settings"],
        group: "account",
        visible: ({ role }) => role("admin")
    },
    {
        id: "features",
        label: "Features",
        description: "Review active modules and available add-ons",
        icon: "sliders-h",
        keywords: ["modules", "add-ons", "capabilities"],
        group: "account",
        visible: ({ role, dev }) => role("admin") && dev
    },
    {
        id: "billing",
        label: "Billing",
        description: "Manage subscriptions and payments",
        icon: "credit-card",
        keywords: ["payments", "subscription", "invoices"],
        group: "account",
        visible: ({ role }) => role("admin")
    },
    {
        id: "users",
        label: "Users",
        description: "Manage team members and permissions",
        icon: "users",
        keywords: ["staff", "team", "people", "permissions"],
        group: "organization",
        visible: ({ role }) => role("admin")
    },
    {
        id: "locations",
        label: "Locations",
        description: "Manage your business locations",
        icon: "map-marked-alt",
        keywords: ["sites", "branches", "premises"],
        group: "organization",
        visible: ({ role }) => role("admin")
    },
    {
        id: "departments",
        label: "Departments",
        description: "Organize your departments",
        icon: "building",
        keywords: ["teams", "areas"],
        group: "organization",
        visible: ({ has, role, dev }) => role("admin") && (dev || has(Flags.DepartmentsModule))
    },
    {
        id: "suppliers",
        label: "Suppliers",
        description: "Manage your suppliers and vendors",
        icon: "industry-alt",
        keywords: ["vendors", "producers"],
        group: "organization",
        visible: ({ has, role }) => role("admin") && has(Flags.SuppliersModule)
    },
    {
        id: "customers",
        label: "Customers",
        description: "Manage your customers and dispatch destinations",
        icon: "users",
        keywords: ["dispatch destinations", "clients"],
        group: "organization",
        visible: ({ has, role }) => role("admin") && has(Flags.DispatchModule)
    },
    {
        id: "auditor-access",
        label: "Auditor access",
        description: "Manage auditor permissions and access",
        icon: "user-shield",
        keywords: ["auditors", "permissions"],
        group: "access",
        visible: ({ role }) => role("admin")
    },
    {
        id: "supplier-access",
        label: "Supplier access",
        description: "Control supplier access to your account",
        icon: "truck",
        keywords: ["supplier login", "vendor access"],
        group: "access",
        visible: ({ has, role }) => role("admin") && has(Flags.SuppliersModule)
    },
    {
        id: "support-pin",
        label: "Support PIN",
        description: "Generate a pin for support verification",
        icon: "key",
        keywords: ["support code", "verification"],
        group: "support",
        visible: ({ role }) => role("admin") || role("auditor")
    },
    {
        id: "data-export",
        label: "Data export",
        description: "Download all your business data",
        icon: "download",
        keywords: ["download data", "backup", "export"],
        group: "support",
        visible: ({ role }) => role("admin")
    },
    {
        id: "legal",
        label: "Legal & privacy",
        description: "View legal documents and privacy policies",
        icon: "shield-alt",
        keywords: ["privacy", "terms", "eula"],
        group: "support",
        visible: ({ role }) => role("admin") || role("auditor")
    }
];

export function visibleSettingsSections(ctx) {
    return settingsSections.filter((section) => section.visible(ctx));
}

export function canAccessSettingsSection(sectionID, ctx) {
    if (!sectionID) return true;
    const section = settingsSections.find((item) => item.id === sectionID);
    return !!section?.visible(ctx);
}
